import { useLocale } from 'next-intl'
import { useMemo } from 'react'
import {
    asyncRequestGoogleAutocompletePlace,
    asyncRequestGoogleAutocompletePlacePredictions,
} from '@/features/google-places/google-places-calls'
import {
    IGoogleMapsPlacesAutocompleteRequest,
    IGooglePlacesSelectOption,
} from '@/features/google-places/google-places-types'
import { GOOGLE_PLACES_COUNTRY_CODES, Locale } from '@/setup/locales'
import { debounce } from '@/utils/functions/debounce'

export type GooglePlacesConfig = {
    googlePlacesRequestConfig?: Omit<IGoogleMapsPlacesAutocompleteRequest, 'input'>
    debounceWait?: number
}

export const useGooglePlacesSelect = (config: GooglePlacesConfig = {}) => {
    const locale = useLocale()

    const loadSelectOptions = useMemo(
        (): ((
            searchValue: string,
            commitId: number,
            setOptions: (options: IGooglePlacesSelectOption[], commitId: number) => void
        ) => void) =>
            debounce(async (searchValue, commitId, setOptions) => {
                if (!searchValue) return

                const response = await asyncRequestGoogleAutocompletePlacePredictions({
                    ...config?.googlePlacesRequestConfig,
                    input: searchValue,
                    // get only restaurants
                    types: ['establishment'],
                    componentRestrictions: {
                        country: [...GOOGLE_PLACES_COUNTRY_CODES, 'at', 'ch'],
                    },
                    language: locale,
                })

                const newOptions = (response?.predictions ?? []).map((prediction) => ({
                    label: prediction.description,
                    value: prediction,
                }))

                setOptions(newOptions, commitId)
            }, config?.debounceWait || 400),
        [config, locale]
    )

    return useMemo(() => ({ loadSelectOptions, loadPlace }), [loadSelectOptions])
}

export type ILoadPlace = (
    placeId: string,
    locale: Locale
) => Promise<google.maps.places.PlaceResult | null>

export const loadPlace: ILoadPlace = (placeId, locale) => {
    return asyncRequestGoogleAutocompletePlace({
        placeId,
        language: locale,
        fields: [
            'address_components',
            'formatted_address',
            'geometry',
            'name',
            'photos',
            'price_level',
            'type',
        ],
    })
}
